import { cn } from "@/lib/cn";

import { SEGMENTS_UP_TO, type Control } from "@/artifacts/controls";

/**
 * The switch between a chart's slices.
 *
 * The server built every slice; this only says which one is on screen. A
 * slider for a long ordered run (years, months) because dragging through
 * time is the point of having them in order; segments for a few, because
 * every choice is then one click and visible at once.
 */
export function ChartControl({
  control,
  value,
  onChange,
}: {
  control: Control;
  value: string;
  onChange: (value: string) => void;
}) {
  const values = control.values ?? [];
  const index = Math.max(0, values.indexOf(value));
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="type-caption text-ink-secondary">{control.label}</span>
      {values.length <= SEGMENTS_UP_TO ? (
        <div
          role="radiogroup"
          aria-label={control.label}
          className="border-border rounded-inner inline-flex overflow-hidden border"
        >
          {values.map((option) => (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={option === value}
              onClick={() => onChange(option)}
              className={cn(
                "type-caption motion-fast border-border px-2 py-1 not-first:border-l",
                option === value
                  ? "bg-accent text-on-accent"
                  : "text-ink-secondary hover:bg-hover hover:text-ink",
              )}
            >
              {option}
            </button>
          ))}
        </div>
      ) : (
        <label className="flex min-w-0 flex-1 items-center gap-2">
          <input
            type="range"
            min={0}
            max={values.length - 1}
            step={1}
            value={index}
            aria-label={control.label}
            aria-valuetext={values[index]}
            onChange={(event) => onChange(values[Number(event.target.value)] ?? value)}
            className="accent-accent min-w-24 flex-1"
          />
          <span className="type-label text-ink tabular-nums">{values[index]}</span>
        </label>
      )}
    </div>
  );
}
